/** The seat-PIN hand-off, one component for the Roster, the Board's queue and a team card: the dialog, the one-time PIN and "given at". */
import type { BoardTeamRow, UNIQUE_TEAM_ROLES } from "@bsim/contracts";
import { useState } from "react";
import { Dialog } from "../theme/Dialog";
import { roleLabel } from "../shell/TopBar";
import { Confirm } from "./BandDialogs";
import type { Act } from "./useAct";
import { lowerFirst } from "../format/labels";

/**
 * "Give a new PIN" for a team's CEO or CRO seat: the old PIN stops working and the new one is shown once, here, to read out to the team.
 * A refusal keeps the dialog open and says why in the api's own words (the F17.6b pattern).
 */
export function SeatPin({ cohortId, team, role, act, label: buttonLabel, className = "gx-btn gx-btn--secondary", testId }: {
  readonly cohortId: string; readonly team: Pick<BoardTeamRow, "teamId" | "name">; readonly role: (typeof UNIQUE_TEAM_ROLES)[number]; readonly act: Act;
  /** The button's words (the Roster names the seat; a queue item says "Give a new PIN"). */ readonly label?: string; readonly className?: string; readonly testId?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [pin, setPin] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // When the last new PIN was given (this page's session): if the dialog was closed before the team had it, the row says so and the facilitator gives another rather than hunting.
  const [givenAt, setGivenAt] = useState<string | null>(null);
  const label = lowerFirst(roleLabel(role));
  return (
    <>
      <button type="button" className={className} data-testid={testId ?? `roster.pin.${team.teamId}.${role}`} onClick={() => { setRefusal(null); setAsking(true); }}>{buttonLabel ?? `Give the ${label} a new PIN`}</button>
      {asking ? (
        <Confirm title={`Give ${team.name}'s ${label} a new PIN?`} testId="dialog.seatPin" confirmLabel="Give a new PIN" onClose={() => setAsking(false)}
          lead="The old PIN stops working. Read the new one to the team; it is shown once."
          onConfirm={async () => {
            const res = await act("facilitatorSeatPinReissue", { cohortId, teamId: team.teamId, role }, undefined, { idempotent: true });
            if (res.ok) { const at = new Date(); setGivenAt(`${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`); setCopied(false); setPin(res.value.pin); return true; }
            setRefusal(res.error.message.trim() === "" ? "The PIN could not be changed." : res.error.message.trim());
            return false;
          }}>
          {refusal === null ? null : <p className="gx-small" role="alert" data-testid="seatPin.refused">{refusal}</p>}
        </Confirm>
      ) : null}
      {givenAt === null ? null : <span className="gx-small gx-faint" data-testid={`roster.pin.${team.teamId}.${role}.given`}>A new PIN was given at {givenAt}</span>}
      {pin === null ? null : (
        <Dialog title={`${team.name}'s ${label} PIN`} testId="dialog.seatPin.shown" onClose={() => setPin(null)}
          lead="Read this to the team now. It is not shown again."
          footer={<><span className="gx-grow" /><button type="button" className="gx-btn gx-btn--secondary" data-testid="seatPin.copy" onClick={() => { void navigator.clipboard?.writeText(pin).then(() => setCopied(true), () => undefined); }}>{copied ? "Copied" : "Copy"}</button><button type="button" className="gx-btn gx-btn--primary" onClick={() => setPin(null)}>Done</button></>}>
          <div className="gx-fac-dbody"><p className="gx-h1 gx-num" data-testid="seatPin.value" style={{ letterSpacing: "0.3em" }}>{pin}</p></div>
        </Dialog>
      )}
    </>
  );
}
